import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Mail, CheckCircle2, AlertCircle, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, already_verified, error
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await fetch(`${API}/guest/verify-email?token=${token}`);
      const data = await response.json();

      if (data.success) {
        if (data.already_verified) {
          setStatus('already_verified');
        } else {
          setStatus('success');
        }
        setMessage(data.message);
        setUser(data.user);
      } else {
        setStatus('error');
        setMessage(data.message || 'Verification failed');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Connection error. Please try again.');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <>
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
            </div>
            <CardTitle className="text-2xl font-bold text-white mb-3">
              Verifying Your Email...
            </CardTitle>
            <CardDescription className="text-gray-400">
              Please wait while we verify your email address
            </CardDescription>
          </>
        );

      case 'success':
        return (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-white mb-3">
              Email Verified! 🎉
            </CardTitle>
            <CardDescription className="text-gray-300 mb-6">
              {message}
            </CardDescription>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <Clock className="w-5 h-5" />
                <span className="font-medium">What happens next?</span>
              </div>
              <p className="text-gray-400 text-sm">
                An admin will review your request and approve your account. 
                Once approved, you'll be able to login and start generating signals.
              </p>
            </div>
          </>
        );

      case 'already_verified':
        return (
          <>
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-yellow-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-white mb-3">
              Already Verified
            </CardTitle>
            <CardDescription className="text-gray-300 mb-6">
              {message}
            </CardDescription>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-gray-400 text-sm">
                Your email is already verified. If you haven't been approved yet, 
                please wait for an admin to review your request.
              </p>
            </div>
          </>
        );

      case 'error':
        return (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-white mb-3">
              Verification Failed
            </CardTitle>
            <CardDescription className="text-red-300 mb-6">
              {message}
            </CardDescription>
            <p className="text-gray-400 text-sm mb-6">
              The verification link may have expired or is invalid. 
              Please try signing up again or contact support.
            </p>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-xl border-white/20 text-center">
        <CardHeader>
          {renderContent()}
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-6"
          >
            Go to Dashboard
          </Button>
          
          {user && (
            <div className="mt-4 text-sm text-gray-400">
              Account: {user.email}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
